import type { CreativeClass, TChannel } from '@feathr/blackbox';
import {
  ADS_CREATIVE_CLASSES,
  CreativeClass as CC,
  DISPLAY_CREATIVE_CLASSES,
  META_CREATIVE_CLASSES,
} from '@feathr/blackbox';

import type {
  ICreativesFilters,
  TChannelFilter,
  TCreativeTypeFilter,
} from './ProjectCreativesPage.types';

/** Maps channel filter values to their creative classes. */
const CHANNEL_CLASSES: Record<TChannelFilter, CreativeClass[]> = {
  display: DISPLAY_CREATIVE_CLASSES,
  meta: META_CREATIVE_CLASSES,
};

/** Maps type filter values to their creative classes. */
const TYPE_CLASSES: Record<TCreativeTypeFilter, CreativeClass[]> = {
  bannersnack: [CC.DisplayBannersnack],
  ad_tag: [CC.DisplayAdTag],
  image: [CC.DisplayImage, CC.FacebookImage],
  video: [CC.DisplayVideo, CC.FacebookVideo],
};

/**
 * Gets the creative classes for a channel filter value.
 *
 * @param channel - The channel filter value
 * @returns The creative classes for that channel, or undefined if no channel
 */
export function getChannelClasses(channel?: TChannelFilter): CreativeClass[] | undefined {
  return channel ? CHANNEL_CLASSES[channel] : undefined;
}

/**
 * Gets the creative classes for a type filter value.
 *
 * @param creativeType - The type filter value
 * @returns The creative classes for that type, or undefined if no type
 */
export function getTypeClasses(creativeType?: TCreativeTypeFilter): CreativeClass[] | undefined {
  return creativeType ? TYPE_CLASSES[creativeType] : undefined;
}

/**
 * Converts the channel filter to the TChannel type used by AdDimensionsSelect.
 *
 * @param channel - The channel filter value
 * @returns The TChannel value, or undefined if no channel
 */
export function getSelectedChannel(channel?: TChannelFilter): TChannel | undefined {
  return channel;
}

/** Result of combining channel and type filters. */
export interface ICombinedClassesResult {
  /** The combined creative classes to filter by. */
  classes: CreativeClass[];
  /** True if the filter combination is impossible (e.g., Meta + Bannersnack). */
  isImpossible: boolean;
}

/**
 * Combines channel and type filters into a single class filter for the API.
 *
 * When both filters are set, returns their intersection. If the intersection is empty
 * (impossible combination like Meta + Bannersnack), returns all classes with isImpossible flag.
 *
 * @param filters - The current filter state
 * @returns The combined classes and whether the combination is impossible
 *
 * @example
 * // Channel: display, Type: image → [DisplayImage]
 * getCombinedClasses({ channel: 'display', creativeType: 'image' })
 */
export function getCombinedClasses(filters: ICreativesFilters): ICombinedClassesResult {
  const channelClasses = getChannelClasses(filters.channel);
  const typeClasses = getTypeClasses(filters.creativeType);

  if (channelClasses && typeClasses) {
    const intersection = channelClasses.filter((cls) => typeClasses.includes(cls));
    if (intersection.length === 0) {
      return { classes: ADS_CREATIVE_CLASSES, isImpossible: true };
    }
    return { classes: intersection, isImpossible: false };
  }
  if (channelClasses) {
    return { classes: channelClasses, isImpossible: false };
  }
  if (typeClasses) {
    return { classes: typeClasses, isImpossible: false };
  }
  return { classes: ADS_CREATIVE_CLASSES, isImpossible: false };
}

/**
 * Checks if any user-applied filters are active.
 *
 * @param filters - The current filter state
 * @returns True if any filter is set
 */
export function hasActiveFilters(filters: ICreativesFilters): boolean {
  return Boolean(
    filters.channel || filters.creativeType || filters.spec || filters.name__icontains,
  );
}
